import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'

export interface PerformanceData {
  period: string
  portfolioReturn: string
  benchmarkReturn: string
  benchmark2Return?: string
  excessReturn: string
  excess2Return?: string
  volatility: string
}

export const usePerformanceData = (portfolio: string) => {
  const [performance, setPerformance] = useState<PerformanceData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log(`🚀 Fetching performance for ${portfolio} (optimized)`)

        // Try to get data from cache first
        console.log(`🔍 Fetching ${portfolio} performance from cache...`)
        
        const { data: cacheData, error: cacheError } = await supabase
          .from('portfolio_performance_cache')
          .select('period, portfolio_return, benchmark_return, benchmark2_return, excess_return, excess2_return, volatility')
          .eq('portfolio', portfolio)
          .order('period')

        if (cacheError) {
          console.error('❌ Cache query error:', cacheError)
          throw new Error(`Cache query failed: ${cacheError.message}`)
        }

        if (cacheData && cacheData.length > 0) {
          console.log(`✅ Found ${cacheData.length} cached performance records for ${portfolio}`)
          
          const performanceData: PerformanceData[] = cacheData.map(item => ({
            period: item.period,
            portfolioReturn: item.portfolio_return || '--',
            benchmarkReturn: item.benchmark_return || '--',
            benchmark2Return: item.benchmark2_return || undefined,
            excessReturn: item.excess_return || '--',
            excess2Return: item.excess2_return || undefined,
            volatility: item.volatility || '--'
          }))

          // Sort in proper chronological order
          const periodOrder = ['1m', '3m', '6m', 'YTD', '1Yr', '3Yrs']
          performanceData.sort((a, b) => {
            const aIndex = periodOrder.indexOf(a.period)
            const bIndex = periodOrder.indexOf(b.period)
            return aIndex - bIndex
          })

          // Check data freshness and trigger background refresh if needed
          const { data: refreshStatus } = await supabase
            .from('data_refresh_status')
            .select('last_refresh, refresh_status')
            .eq('data_source', 'portfolio_performance')
            .single()

          const lastRefresh = refreshStatus?.last_refresh ? new Date(refreshStatus.last_refresh) : new Date(0)
          const hoursSinceRefresh = (Date.now() - lastRefresh.getTime()) / (1000 * 60 * 60)
          
          // Trigger background refresh if data is stale (>1 hour)
          if (hoursSinceRefresh > 1 && refreshStatus?.refresh_status !== 'running') {
            console.log(`🔄 Triggering background performance refresh for stale data`)
            
            // Background refresh - don't await
            supabase.functions.invoke('portfolio-performance-refresh')
              .catch(err => console.log('Background performance refresh failed:', err))
          }

          setPerformance(performanceData)
          setError(null)
          return
        }

        // If no cache data, trigger immediate refresh and fall back to v2 function
        console.log(`⚠️ No cached performance data for ${portfolio}, triggering refresh and using fallback...`)
        
        // Trigger immediate refresh for future requests - don't await
        supabase.functions.invoke('portfolio-performance-refresh')
          .catch(err => console.log('Background performance refresh failed:', err))
        
        const { data, error: fetchError } = await supabase.functions.invoke(
          'portfolio-performance-v2',
          {
            body: { portfolio }
          }
        )

        if (fetchError) {
          throw fetchError
        }

        if (data.error) {
          console.warn('Performance data error:', data.error)
          setError(data.error)  
        } else {
          setError(null)
        }

        // Sort fallback data in proper chronological order too
        const fallbackData = data.performance || []
        const periodOrder = ['1m', '3m', '6m', 'YTD', '1Yr', '3Yrs']
        fallbackData.sort((a, b) => {
          const aIndex = periodOrder.indexOf(a.period)
          const bIndex = periodOrder.indexOf(b.period)
          return aIndex - bIndex
        })
        setPerformance(fallbackData)
        
      } catch (err) {
        console.error('Error fetching performance data:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch performance data')
        setPerformance([])
      } finally {
        setLoading(false)
      }
    }

    if (portfolio) {
      fetchPerformance()
    }
  }, [portfolio])

  return { performance, loading, error }
}